import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { QRCodeSVG } from "qrcode.react"
import {
  Loader2, Save, User, Globe, Key, Eye, EyeOff,
  CheckCircle2, Unlink, ExternalLink, Mail, Receipt, MessageSquare, Video,
  QrCode, Smartphone, Copy, Plus, Trash2, FileSpreadsheet, Terminal,
} from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { useToast } from "@/hooks/use-toast"
import { api } from "@/services/api"
import { useModuleStore } from "@/store/moduleStore"

// Häufig verwendete Zeitzonen
const COMMON_TIMEZONES = [
  { value: "__system__", label: "System-Standard verwenden" },
  { value: "Europe/Berlin", label: "Berlin (Europe/Berlin)" },
  { value: "Europe/Vienna", label: "Wien (Europe/Vienna)" },
  { value: "Europe/Zurich", label: "Zürich (Europe/Zurich)" },
  { value: "Europe/London", label: "London (Europe/London)" },
  { value: "Europe/Paris", label: "Paris (Europe/Paris)" },
  { value: "Europe/Amsterdam", label: "Amsterdam (Europe/Amsterdam)" },
  { value: "Europe/Brussels", label: "Brüssel (Europe/Brussels)" },
  { value: "America/New_York", label: "New York (America/New_York)" },
  { value: "America/Los_Angeles", label: "Los Angeles (America/Los_Angeles)" },
  { value: "America/Chicago", label: "Chicago (America/Chicago)" },
  { value: "Asia/Tokyo", label: "Tokyo (Asia/Tokyo)" },
  { value: "Asia/Shanghai", label: "Shanghai (Asia/Shanghai)" },
  { value: "Australia/Sydney", label: "Sydney (Australia/Sydney)" },
  { value: "UTC", label: "UTC" },
]

const EMAIL_SUMMARY_GOOGLE_SCOPES = ["gmail", "drive_file", "sheets", "apps_script"]

interface UserProfileDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function UserProfileDialog({ open, onOpenChange }: UserProfileDialogProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [timezone, setTimezone] = useState("__system__")
  const [gmailMonitoring, setGmailMonitoring] = useState(false)
  const [meetUrl, setMeetUrl] = useState("")
  const [delegatedMailbox, setDelegatedMailbox] = useState("")
  const [apiTokenName, setApiTokenName] = useState("Projektbase Sync")
  const [createdApiToken, setCreatedApiToken] = useState<string | null>(null)
  const [sshKeyLabel, setSshKeyLabel] = useState("MacBook")
  const [sshPublicKey, setSshPublicKey] = useState("")
  const [ksuiteToken, setKsuiteToken] = useState("")
  const [showKsuiteToken, setShowKsuiteToken] = useState(false)
  const [easybillKey, setEasybillKey] = useState("")
  const [showEasybillKey, setShowEasybillKey] = useState(false)
  const [easybillLoginOptions, setEasybillLoginOptions] = useState<import("@/types/hr").EasybillLoginOption[]>([])
  const [easybillSelectedLogin, setEasybillSelectedLogin] = useState("")
  const isMatrixEnabled = useModuleStore((state) => state.isModuleEnabled("matrix"))
  const isKsuiteEnabled = useModuleStore((state) => state.isModuleEnabled("ksuite"))
  const isMattermostEnabled = useModuleStore((state) => state.isModuleEnabled("mattermost"))

  // Fetch current user profile
  const { data: profile, isLoading } = useQuery({
    queryKey: ["user-profile"],
    queryFn: () => api.auth.getProfile(),
    enabled: open,
  })

  // Fetch system timezone for display
  const { data: systemSettings } = useQuery({
    queryKey: ["system-settings"],
    queryFn: () => api.settings.getSystem(),
    enabled: open,
  })

  // kSuite connection status
  const { data: ksuiteStatus } = useQuery({
    queryKey: ["auth", "me", "ksuite"],
    queryFn: () => api.auth.getKsuiteStatus(),
    enabled: open && isKsuiteEnabled,
  })

  const { data: matrixStatus } = useQuery({
    queryKey: ["auth", "me", "matrix"],
    queryFn: () => api.auth.getMatrixStatus(),
    enabled: open && isMatrixEnabled,
  })

  const { data: googleOAuthStatus } = useQuery({
    queryKey: ["oauth", "google", "status"],
    queryFn: () => api.oauth.getGoogleStatus(),
    enabled: open,
  })

  const { data: emailAccounts } = useQuery({
    queryKey: ["auth", "me", "email-accounts"],
    queryFn: () => api.auth.getEmailAccounts(),
    enabled: open,
  })

  const { data: apiTokens = [] } = useQuery({
    queryKey: ["auth", "me", "api-tokens"],
    queryFn: () => api.auth.getApiTokens(),
    enabled: open,
  })

  const { data: sshAccess } = useQuery({
    queryKey: ["auth", "me", "ssh-access"],
    queryFn: () => api.auth.getSshAccess(),
    enabled: open,
  })

  const { data: emailSummarySetup } = useQuery({
    queryKey: ["auth", "me", "email-summary-google-setup"],
    queryFn: () => api.auth.getEmailSummaryGoogleSetup(),
    enabled: open,
  })

  const delegatedGoogleMailboxes = (emailAccounts || []).filter(
    (account) =>
      account.provider === "google" &&
      account.accountType === "delegate" &&
      account.isActive
  )
  const emailSummaryNeedsScopes = (emailSummarySetup?.missingScopes?.length || 0) > 0
  const sshIdentityPlaceholder = "/Users/<mac-user>/.ssh/id_ed25519"
  const sshClaudeFolder = "vault"
  const sshTerminalCommand = sshAccess
    ? `ssh -o IdentitiesOnly=yes -o PreferredAuthentications=publickey -p ${sshAccess.sshPort} -i ${sshIdentityPlaceholder} ${sshAccess.sshTarget}`
    : ""
  const sshConfigSnippet = sshAccess
    ? [
        "Host erp-projektbase",
        `  HostName ${sshAccess.sshHost}`,
        `  User ${sshAccess.linuxUsername}`,
        `  Port ${sshAccess.sshPort}`,
        `  IdentityFile ${sshIdentityPlaceholder}`,
        "  IdentitiesOnly yes",
        "  PreferredAuthentications publickey",
      ].join("\n")
    : ""

  const copyToClipboard = (text: string, title: string) => {
    if (!text) return
    void navigator.clipboard.writeText(text)
    toast({ title })
  }

  useEffect(() => {
    const success = searchParams.get("google_success")
    const error = searchParams.get("google_error")

    if (success === "true") {
      toast({
        title: "Google-Konto verbunden",
        description: "Dein Gmail/Google-Konto wurde erfolgreich verbunden.",
      })
      queryClient.invalidateQueries({ queryKey: ["oauth", "google", "status"] })
      queryClient.invalidateQueries({ queryKey: ["auth", "me", "email-accounts"] })
      queryClient.invalidateQueries({ queryKey: ["auth", "me", "email-summary-google-setup"] })
    } else if (error) {
      toast({
        title: "Google-Verbindung fehlgeschlagen",
        description: decodeURIComponent(error),
        variant: "destructive",
      })
    } else {
      return
    }

    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete("google_success")
    nextParams.delete("google_error")
    nextParams.delete("profile")
    setSearchParams(nextParams, { replace: true })
  }, [searchParams, setSearchParams, toast, queryClient])

  const matrixActivateMutation = useMutation({
    mutationFn: () => api.auth.activateMatrix(),
    onSuccess: () => {
      toast({ title: "Matrix-Zugang aktiviert" })
      queryClient.invalidateQueries({ queryKey: ["auth", "me", "matrix"] })
    },
    onError: (err: any) => {
      toast({
        title: "Matrix konnte nicht aktiviert werden",
        description: err?.response?.data?.detail || "Bitte Matrix-Konfiguration pruefen.",
        variant: "destructive",
      })
    },
  })

  const googleConnectMutation = useMutation({
    mutationFn: async () => {
      const redirectUrl = new URL(window.location.href)
      redirectUrl.searchParams.set("profile", "google")
      redirectUrl.searchParams.delete("google_success")
      redirectUrl.searchParams.delete("google_error")
      return api.oauth.authorizeGoogle(EMAIL_SUMMARY_GOOGLE_SCOPES, redirectUrl.toString())
    },
    onSuccess: (data) => {
      window.location.href = data.authorizationUrl
    },
    onError: (err: any) => {
      toast({
        title: "Google OAuth konnte nicht gestartet werden",
        description: err?.response?.data?.detail || "Bitte OAuth-Konfiguration pruefen.",
        variant: "destructive",
      })
    },
  })

  const googleDisconnectMutation = useMutation({
    mutationFn: () => api.oauth.revokeGoogle(),
    onSuccess: (result) => {
      if (result.success) {
        toast({ title: "Google-Konto getrennt" })
        queryClient.invalidateQueries({ queryKey: ["oauth", "google", "status"] })
        queryClient.invalidateQueries({ queryKey: ["auth", "me", "email-accounts"] })
        queryClient.invalidateQueries({ queryKey: ["auth", "me", "email-summary-google-setup"] })
      } else {
        toast({
          title: "Google-Konto konnte nicht getrennt werden",
          description: result.message,
          variant: "destructive",
        })
      }
    },
    onError: () => {
      toast({
        title: "Google-Konto konnte nicht getrennt werden",
        variant: "destructive",
      })
    },
  })

  const addDelegatedMailboxMutation = useMutation({
    mutationFn: (email: string) => api.auth.addDelegatedEmailAccount(email),
    onSuccess: () => {
      toast({ title: "Delegierte Mailbox hinzugefuegt" })
      queryClient.invalidateQueries({ queryKey: ["auth", "me", "email-accounts"] })
      setDelegatedMailbox("")
    },
    onError: (err: any) => {
      toast({
        title: "Mailbox konnte nicht hinzugefuegt werden",
        description: err?.response?.data?.detail || "Bitte Adresse und Google-Verbindung pruefen.",
        variant: "destructive",
      })
    },
  })

  const emailSummarySetupMutation = useMutation({
    mutationFn: () => api.auth.setupEmailSummaryGoogleSheet(),
    onSuccess: (result) => {
      toast({
        title: "Google Sheet vorbereitet",
        description: result.scriptError
          ? "Sheet wurde erstellt. Apps Script muss ggf. manuell geprueft werden."
          : "Parameter, Prompt und Apps Script wurden angelegt.",
      })
      queryClient.invalidateQueries({ queryKey: ["auth", "me", "email-summary-google-setup"] })
      if (result.spreadsheetUrl) {
        window.open(result.spreadsheetUrl, "_blank", "noopener,noreferrer")
      }
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail
      toast({
        title: "Google Sheet konnte nicht vorbereitet werden",
        description: typeof detail === "string" ? detail : detail?.message || err?.message || "Bitte Google-Rechte pruefen.",
        variant: "destructive",
      })
    },
  })

  const deleteEmailAccountMutation = useMutation({
    mutationFn: (accountId: string) => api.auth.deleteEmailAccount(accountId),
    onSuccess: () => {
      toast({ title: "Mailbox entfernt" })
      queryClient.invalidateQueries({ queryKey: ["auth", "me", "email-accounts"] })
    },
    onError: (err: any) => {
      toast({
        title: "Mailbox konnte nicht entfernt werden",
        description: err?.response?.data?.detail || "Bitte spaeter erneut versuchen.",
        variant: "destructive",
      })
    },
  })

  const createApiTokenMutation = useMutation({
    mutationFn: () => api.auth.createApiToken({ name: apiTokenName.trim() || "Projektbase Sync" }),
    onSuccess: (result) => {
      setCreatedApiToken(result.token)
      setApiTokenName("Projektbase Sync")
      queryClient.invalidateQueries({ queryKey: ["auth", "me", "api-tokens"] })
      toast({
        title: "API-Token erstellt",
        description: "Der Token wird nur jetzt im Klartext angezeigt.",
      })
    },
    onError: (err: any) => {
      toast({
        title: "API-Token konnte nicht erstellt werden",
        description: err?.response?.data?.detail || "Bitte spaeter erneut versuchen.",
        variant: "destructive",
      })
    },
  })

  const revokeApiTokenMutation = useMutation({
    mutationFn: (tokenId: string) => api.auth.revokeApiToken(tokenId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth", "me", "api-tokens"] })
      toast({ title: "API-Token widerrufen" })
    },
    onError: (err: any) => {
      toast({
        title: "API-Token konnte nicht widerrufen werden",
        description: err?.response?.data?.detail || "Bitte spaeter erneut versuchen.",
        variant: "destructive",
      })
    },
  })

  const activateSshAccessMutation = useMutation({
    mutationFn: () => api.auth.activateSshAccess(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth", "me", "ssh-access"] })
      toast({ title: "SSH-Zugang vorbereitet" })
    },
    onError: (err: any) => {
      toast({
        title: "SSH-Zugang konnte nicht vorbereitet werden",
        description: err?.response?.data?.detail || "Bitte spaeter erneut versuchen.",
        variant: "destructive",
      })
    },
  })

  const testSshAccessMutation = useMutation({
    mutationFn: () => api.auth.testSshAccess(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["auth", "me", "ssh-access"] })
      toast({
        title: result.ok ? "SSH-Zugang bereit" : "SSH-Zugang pruefen",
        description: result.hints[0] || "Der serverseitige Test wurde ausgefuehrt.",
        variant: result.ok ? undefined : "destructive",
      })
    },
    onError: (err: any) => {
      toast({
        title: "SSH-Zugang konnte nicht getestet werden",
        description: err?.response?.data?.detail || "Bitte spaeter erneut versuchen.",
        variant: "destructive",
      })
    },
  })

  const addSshKeyMutation = useMutation({
    mutationFn: () => api.auth.addSshKey({
      label: sshKeyLabel.trim() || "SSH-Key",
      publicKey: sshPublicKey.trim(),
    }),
    onSuccess: () => {
      setSshKeyLabel("MacBook")
      setSshPublicKey("")
      queryClient.invalidateQueries({ queryKey: ["auth", "me", "ssh-access"] })
      toast({ title: "SSH Public Key hinterlegt" })
    },
    onError: (err: any) => {
      toast({
        title: "SSH Public Key konnte nicht gespeichert werden",
        description: err?.response?.data?.detail || "Bitte Public Key pruefen.",
        variant: "destructive",
      })
    },
  })

  const deleteSshKeyMutation = useMutation({
    mutationFn: (keyId: string) => api.auth.deleteSshKey(keyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth", "me", "ssh-access"] })
      toast({ title: "SSH Public Key entfernt" })
    },
    onError: (err: any) => {
      toast({
        title: "SSH Public Key konnte nicht entfernt werden",
        description: err?.response?.data?.detail || "Bitte spaeter erneut versuchen.",
        variant: "destructive",
      })
    },
  })

  const ksuiteSaveMutation = useMutation({
    mutationFn: (token: string) => api.auth.saveKsuiteToken(token),
    onSuccess: (data) => {
      toast({
        title: "kSuite verbunden",
        description: `Verbunden als: ${data.displayName || data.email}`,
      })
      queryClient.invalidateQueries({ queryKey: ["auth", "me", "ksuite"] })
      setKsuiteToken("")
    },
    onError: () => {
      toast({
        title: "Token ungueltig",
        description: "API-Token ungueltig oder Scope 'user_info' fehlt.",
        variant: "destructive",
      })
    },
  })

  const ksuiteDisconnectMutation = useMutation({
    mutationFn: () => api.auth.deleteKsuiteToken(),
    onSuccess: () => {
      toast({ title: "kSuite getrennt" })
      queryClient.invalidateQueries({ queryKey: ["auth", "me", "ksuite"] })
    },
  })

  // Easybill API-Key
  const { data: myEmployee } = useQuery<any>({
    queryKey: ["hr-employee-me"],
    queryFn: async () => { try { return await api.hr.getMyEmployee() } catch { return null } },
    enabled: open,
  })

  const easybillSaveMutation = useMutation({
    mutationFn: (apiKey: string | null) => api.hr.setMyEasybillKey(apiKey),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["hr-employee-me"] })
      setEasybillKey("")
      if (!res.easybillApiKeySet) {
        setEasybillLoginOptions([])
        setEasybillSelectedLogin("")
        toast({ title: "Easybill getrennt", description: "API-Key entfernt." })
        return
      }
      if (res.needsSelection) {
        // Login nicht eindeutig per E-Mail zuordenbar -> Auswahl anzeigen
        setEasybillLoginOptions(res.logins)
        setEasybillSelectedLogin("")
        toast({
          title: "Login auswählen",
          description: "Der Key konnte keinem Easybill-Login eindeutig zugeordnet werden. Bitte deinen Login auswählen.",
        })
        return
      }
      setEasybillLoginOptions([])
      setEasybillSelectedLogin("")
      toast({ title: "Easybill verbunden", description: res.easybillUserName ? `Verbunden als ${res.easybillUserName}` : "API-Key gespeichert." })
    },
    onError: (err: any) => {
      toast({ title: "Fehler", description: err?.response?.data?.detail || "API-Key ungueltig.", variant: "destructive" })
    },
  })

  const easybillLoginMutation = useMutation({
    mutationFn: (loginId: string) => api.hr.setMyEasybillLogin(loginId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["hr-employee-me"] })
      setEasybillLoginOptions([])
      setEasybillSelectedLogin("")
      toast({ title: "Easybill verbunden", description: res.easybillUserName ? `Verbunden als ${res.easybillUserName}` : "Login gespeichert." })
    },
    onError: (err: any) => {
      toast({ title: "Fehler", description: err?.response?.data?.detail || "Login konnte nicht gespeichert werden.", variant: "destructive" })
    },
  })

  useEffect(() => {
    if (profile) {
      setFirstName(profile.firstName || "")
      setLastName(profile.lastName || "")
      setTimezone(profile.timezone || "__system__")
      setGmailMonitoring(profile.gmailMonitoringEnabled ?? false)
      setMeetUrl(profile.meetUrl || "")
    }
  }, [profile])

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: { firstName?: string; lastName?: string; timezone?: string | null; gmailMonitoringEnabled?: boolean; meetUrl?: string | null }) => {
      return api.auth.updateProfile(data)
    },
    onSuccess: () => {
      toast({
        title: "Profil aktualisiert",
        description: "Deine Änderungen wurden gespeichert.",
      })
      queryClient.invalidateQueries({ queryKey: ["user-profile"] })
      queryClient.invalidateQueries({ queryKey: ["current-user"] })
      onOpenChange(false)
    },
    onError: () => {
      toast({
        title: "Fehler",
        description: "Das Profil konnte nicht aktualisiert werden.",
        variant: "destructive",
      })
    },
  })

  const handleSave = () => {
    updateMutation.mutate({
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      timezone: timezone === "__system__" ? null : timezone,
      gmailMonitoringEnabled: gmailMonitoring,
      meetUrl: meetUrl.trim() ? meetUrl.trim() : null,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[860px]">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Mein Profil
          </DialogTitle>
          <DialogDescription>
            Bearbeite deine persönlichen Einstellungen
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-6">
            {/* Name Section */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">Vorname</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Max"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">Nachname</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Mustermann"
                />
              </div>
            </div>

            <Separator />

            {/* Timezone Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Globe className="h-4 w-4" />
                Regionale Einstellungen
              </div>

              <div className="space-y-2">
                <Label htmlFor="timezone">Zeitzone</Label>
                <Select value={timezone} onValueChange={setTimezone}>
                  <SelectTrigger>
                    <SelectValue placeholder="Zeitzone auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMON_TIMEZONES.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {timezone === "__system__" && systemSettings?.timezone && (
                  <p className="text-xs text-muted-foreground">
                    System-Standard: {systemSettings.timezone}
                  </p>
                )}
              </div>
            </div>

            <Separator />

            {/* Meeting-Raum */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Video className="h-4 w-4" />
                Mein Meeting-Raum
              </div>
              <div className="space-y-2">
                <Label htmlFor="meetUrl">Meet-Link</Label>
                <Input
                  id="meetUrl"
                  type="url"
                  value={meetUrl}
                  onChange={(e) => setMeetUrl(e.target.value)}
                  placeholder="https://meet.google.com/xxx-xxxx-xxx"
                />
                <p className="text-xs text-muted-foreground">
                  Dein persoenlicher Meeting-Raum. Wird als Ziel fuer Klingel-Links
                  in Organisationen verwendet.
                </p>
              </div>
            </div>

            <Separator />

            {/* API Tokens */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Key className="h-4 w-4" />
                API-Tokens
              </div>

              <div className="space-y-3 rounded-lg border p-3">
                <div className="space-y-1">
                  <Label className="text-sm">Projektbase Sync Token</Label>
                  <p className="text-xs text-muted-foreground">
                    Fuer lokale Sync-Tools wie Claude Code Projektbase Sync.
                  </p>
                </div>

                {createdApiToken && (
                  <div className="space-y-2 rounded-md bg-muted/40 p-3">
                    <Label className="text-xs">Neuer Token, nur einmal sichtbar</Label>
                    <div className="flex gap-2">
                      <Input value={createdApiToken} readOnly className="font-mono text-xs" />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          navigator.clipboard.writeText(createdApiToken)
                          toast({ title: "API-Token kopiert" })
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    value={apiTokenName}
                    onChange={(e) => setApiTokenName(e.target.value)}
                    placeholder="Projektbase Sync"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => createApiTokenMutation.mutate()}
                    disabled={createApiTokenMutation.isPending}
                  >
                    {createApiTokenMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                    Erzeugen
                  </Button>
                </div>

                {apiTokens.length > 0 && (
                  <div className="space-y-2">
                    {apiTokens.map((token) => (
                      <div
                        key={token.id}
                        className="flex items-center justify-between gap-2 rounded-md bg-muted/40 px-3 py-2"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-medium">{token.name}</span>
                            {token.revokedAt ? (
                              <Badge variant="secondary" className="text-xs">Widerrufen</Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs text-green-600 border-green-300">Aktiv</Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {token.tokenPrefix}... erstellt am {new Date(token.createdAt).toLocaleDateString("de-DE")}
                          </p>
                        </div>
                        {!token.revokedAt && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => revokeApiTokenMutation.mutate(token.id)}
                            disabled={revokeApiTokenMutation.isPending}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Projektbase SSH */}
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Terminal className="h-4 w-4" />
                  Claude Code SSH
                </div>
                {sshAccess?.enabled ? (
                  <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                    <CheckCircle2 className="mr-1 h-3 w-3" />
                    {sshAccess.activeKeyCount} Key{sshAccess.activeKeyCount === 1 ? "" : "s"}
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs">Nicht aktiv</Badge>
                )}
              </div>

              <div className="space-y-3 rounded-lg border p-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <Label className="text-sm">Projektbase-SSH-Benutzer</Label>
                    <p className="text-xs text-muted-foreground">
                      Fuer Claude Code auf dem ERP-Server. Hinterlege pro Rechner einen eigenen Public Key.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => activateSshAccessMutation.mutate()}
                      disabled={activateSshAccessMutation.isPending}
                    >
                      {activateSshAccessMutation.isPending ? (
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      ) : (
                        <Terminal className="mr-1 h-3 w-3" />
                      )}
                      Vorbereiten
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => testSshAccessMutation.mutate()}
                      disabled={testSshAccessMutation.isPending}
                    >
                      {testSshAccessMutation.isPending ? (
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      ) : (
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                      )}
                      Zugang testen
                    </Button>
                  </div>
                </div>

                {sshAccess && (
                  <div className="grid gap-2 rounded-md bg-muted/40 p-3 text-xs sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label className="text-xs">SSH-Host</Label>
                      <div className="flex gap-2">
                        <Input value={sshAccess.sshTarget} readOnly className="font-mono text-xs" />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => copyToClipboard(sshAccess.sshTarget, "SSH-Host kopiert")}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Port</Label>
                      <Input value={String(sshAccess.sshPort)} readOnly className="font-mono text-xs" />
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-xs">Ordner in Claude Code</Label>
                      <div className="flex gap-2">
                        <Input value={sshClaudeFolder} readOnly className="font-mono text-xs" />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => copyToClipboard(sshClaudeFolder, "Claude-Code-Ordner kopiert")}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-xs">Terminal-Test</Label>
                      <div className="flex gap-2">
                        <Input value={sshTerminalCommand} readOnly className="font-mono text-xs" />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => copyToClipboard(sshTerminalCommand, "SSH-Testbefehl kopiert")}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-xs">Server-Workspace</Label>
                      <div className="flex gap-2">
                        <Input value={sshAccess.workspacePath} readOnly className="font-mono text-xs" />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => copyToClipboard(sshAccess.workspacePath, "Workspace-Pfad kopiert")}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-xs">SSH config</Label>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(sshConfigSnippet, "SSH config kopiert")}
                        >
                          <Copy className="mr-1 h-3 w-3" />
                          Kopieren
                        </Button>
                      </div>
                      <Textarea
                        value={sshConfigSnippet}
                        readOnly
                        rows={7}
                        className="font-mono text-xs"
                      />
                    </div>
                  </div>
                )}

                {testSshAccessMutation.data && (
                  <div
                    className={
                      testSshAccessMutation.data.ok
                        ? "space-y-2 rounded-md border border-green-200 bg-green-50/70 p-3"
                        : "space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-3"
                    }
                  >
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <CheckCircle2
                        className={
                          testSshAccessMutation.data.ok
                            ? "h-4 w-4 text-green-600"
                            : "h-4 w-4 text-destructive"
                        }
                      />
                      {testSshAccessMutation.data.ok
                        ? "Serverseitiger SSH-Zugang ist bereit"
                        : "Serverseitiger SSH-Zugang ist noch unvollstaendig"}
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {testSshAccessMutation.data.checks.map((check) => (
                        <div key={check.name} className="flex items-start gap-2 text-xs">
                          <Badge
                            variant={check.ok ? "outline" : "secondary"}
                            className={check.ok ? "border-green-300 text-green-700" : ""}
                          >
                            {check.ok ? "OK" : "Pruefen"}
                          </Badge>
                          <span className="text-muted-foreground">{check.message}</span>
                        </div>
                      ))}
                    </div>
                    {testSshAccessMutation.data.hints.length > 0 && (
                      <div className="space-y-1 text-xs text-muted-foreground">
                        {testSshAccessMutation.data.hints.map((hint) => (
                          <p key={hint}>{hint}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-sm">Neuen Public Key hinzufuegen</Label>
                  <div className="grid gap-2 sm:grid-cols-[180px_minmax(0,1fr)]">
                    <Input
                      value={sshKeyLabel}
                      onChange={(e) => setSshKeyLabel(e.target.value)}
                      placeholder="MacBook"
                    />
                    <Textarea
                      value={sshPublicKey}
                      onChange={(e) => setSshPublicKey(e.target.value)}
                      rows={3}
                      placeholder="ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAA... hans@macbook"
                      className="font-mono text-xs"
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => addSshKeyMutation.mutate()}
                      disabled={!sshPublicKey.trim() || addSshKeyMutation.isPending}
                    >
                      {addSshKeyMutation.isPending ? (
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      ) : (
                        <Plus className="mr-1 h-3 w-3" />
                      )}
                      Key speichern
                    </Button>
                  </div>
                </div>

                {(sshAccess?.keys || []).length > 0 && (
                  <div className="space-y-2">
                    {sshAccess?.keys.map((key) => (
                      <div
                        key={key.id}
                        className="flex items-center justify-between gap-2 rounded-md bg-muted/40 px-3 py-2"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-medium">{key.label}</span>
                            {key.revokedAt ? (
                              <Badge variant="secondary" className="text-xs">Entfernt</Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs text-green-600 border-green-300">Aktiv</Badge>
                            )}
                          </div>
                          <p className="truncate font-mono text-xs text-muted-foreground">
                            {key.keyType} · {key.fingerprint}
                          </p>
                        </div>
                        {!key.revokedAt && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteSshKeyMutation.mutate(key.id)}
                            disabled={deleteSshKeyMutation.isPending}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* E-Mail-Ueberwachung */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Mail className="h-4 w-4" />
                E-Mail-Ueberwachung
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <Label className="text-sm">Gmail-Postfach ueberwachen</Label>
                  <p className="text-xs text-muted-foreground">
                    Eingehende E-Mails aus verbundenen Google-Mailboxen werden automatisch als Tickets erfasst.
                  </p>
                </div>
                <Switch
                  checked={gmailMonitoring}
                  onCheckedChange={setGmailMonitoring}
                />
              </div>

              <div className="rounded-lg border p-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm">Zusaetzliches Google-Konto</Label>
                      {googleOAuthStatus?.isConnected && (
                        <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                          <CheckCircle2 className="mr-1 h-3 w-3" />
                          Verbunden
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {googleOAuthStatus?.isConnected
                        ? `Verbunden als ${googleOAuthStatus.accountEmail || "Google-Konto"}`
                        : googleOAuthStatus?.isConfigured
                          ? "Verbinde ein externes oder zusaetzliches Gmail-Konto."
                          : "Google OAuth ist noch nicht durch einen Admin aktiviert."}
                    </p>
                  </div>

                  {googleOAuthStatus?.isConnected ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => googleDisconnectMutation.mutate()}
                      disabled={googleDisconnectMutation.isPending}
                      className="text-destructive hover:text-destructive"
                    >
                      {googleDisconnectMutation.isPending ? (
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      ) : (
                        <Unlink className="mr-1 h-3 w-3" />
                      )}
                      Trennen
                    </Button>
                  ) : googleOAuthStatus?.isConfigured ? (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => googleConnectMutation.mutate()}
                      disabled={googleConnectMutation.isPending}
                    >
                      {googleConnectMutation.isPending ? (
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      ) : (
                        <ExternalLink className="mr-1 h-3 w-3" />
                      )}
                      Verbinden
                    </Button>
                  ) : null}
                </div>
              </div>

              <div className="space-y-3 rounded-lg border p-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm">Gmail-Briefing per Google Sheet</Label>
                      {emailSummarySetup?.configured && (
                        <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                          <CheckCircle2 className="mr-1 h-3 w-3" />
                          Bereit
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Erstellt dein persoenliches Setup-Sheet in Google Drive und verknuepft es mit deinem Google-Konto.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {googleOAuthStatus?.isConfigured && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => googleConnectMutation.mutate()}
                        disabled={googleConnectMutation.isPending}
                      >
                        {googleConnectMutation.isPending ? (
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        ) : (
                          <ExternalLink className="mr-1 h-3 w-3" />
                        )}
                        Google-Rechte
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => emailSummarySetupMutation.mutate()}
                      disabled={!emailSummarySetup || emailSummaryNeedsScopes || emailSummarySetupMutation.isPending}
                    >
                      {emailSummarySetupMutation.isPending ? (
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      ) : (
                        <FileSpreadsheet className="mr-1 h-3 w-3" />
                      )}
                      {emailSummarySetup?.configured ? "Aktualisieren" : "Einrichten"}
                    </Button>
                    {emailSummarySetup?.spreadsheetUrl && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={emailSummarySetup.spreadsheetUrl} target="_blank" rel="noreferrer">
                          <ExternalLink className="mr-1 h-3 w-3" />
                          Sheet
                        </a>
                      </Button>
                    )}
                    {emailSummarySetup?.scriptUrl && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={emailSummarySetup.scriptUrl} target="_blank" rel="noreferrer">
                          <ExternalLink className="mr-1 h-3 w-3" />
                          Script
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
                {emailSummarySetup?.scriptError && (
                  <p className="text-xs text-amber-600">
                    Apps Script konnte nicht automatisch aktualisiert werden: {emailSummarySetup.scriptError}
                  </p>
                )}
                {emailSummarySetup?.webhookTokenSuffix && (
                  <p className="text-xs text-muted-foreground">
                    Webhook-Token endet auf ...{emailSummarySetup.webhookTokenSuffix}
                  </p>
                )}
              </div>

              <div className="space-y-3 rounded-lg border p-3">
                <div className="space-y-1">
                  <Label className="text-sm">Delegierte Google-Mailboxen</Label>
                  <p className="text-xs text-muted-foreground">
                    Diese Mailboxen werden zusammen mit deinem verbundenen Google-Konto abgefragt.
                  </p>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    type="email"
                    value={delegatedMailbox}
                    onChange={(e) => setDelegatedMailbox(e.target.value)}
                    placeholder="support@example.com"
                    disabled={!googleOAuthStatus?.isConnected}
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => addDelegatedMailboxMutation.mutate(delegatedMailbox.trim())}
                    disabled={
                      !googleOAuthStatus?.isConnected ||
                      !delegatedMailbox.trim() ||
                      addDelegatedMailboxMutation.isPending
                    }
                  >
                    {addDelegatedMailboxMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                    Hinzufuegen
                  </Button>
                </div>

                {delegatedGoogleMailboxes.length > 0 && (
                  <div className="space-y-2">
                    {delegatedGoogleMailboxes.map((account) => (
                      <div
                        key={account.id}
                        className="flex items-center justify-between gap-2 rounded-md bg-muted/40 px-3 py-2"
                      >
                        <span className="min-w-0 truncate text-sm">{account.email}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteEmailAccountMutation.mutate(account.id)}
                          disabled={deleteEmailAccountMutation.isPending}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {isMatrixEnabled && (
              <>
            <Separator />

            {/* Matrix */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <QrCode className="h-4 w-4" />
                  Matrix
                </div>
                {matrixStatus?.matrixUserId && (
                  <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                    <CheckCircle2 className="mr-1 h-3 w-3" />
                    Aktiv
                  </Badge>
                )}
              </div>

              {matrixStatus?.matrixUserId ? (
                <div className="grid gap-4 rounded-lg border p-3 sm:grid-cols-[auto_minmax(0,1fr)]">
                  {matrixStatus.elementMobileUrl && (
                    <div className="rounded-md bg-white p-2">
                      <QRCodeSVG
                        value={matrixStatus.elementMobileUrl}
                        size={136}
                        level="M"
                        marginSize={2}
                        title={`Element Login ${matrixStatus.matrixUserId}`}
                      />
                    </div>
                  )}
                  <div className="min-w-0 space-y-2">
                    <Label>Matrix-ID</Label>
                    <div className="flex gap-2">
                      <Input value={matrixStatus.matrixUserId} readOnly className="font-mono text-sm" />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          navigator.clipboard.writeText(matrixStatus.matrixUserId || "")
                          toast({ title: "Matrix-ID kopiert" })
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {matrixStatus.cinnyWebUrl && (
                        <Button variant="outline" size="sm" asChild>
                          <a href={matrixStatus.cinnyWebUrl} target="_blank" rel="noreferrer">
                            <MessageSquare className="h-4 w-4" />
                            Cinny oeffnen
                          </a>
                        </Button>
                      )}
                      {matrixStatus.elementMobileUrl && (
                        <Button variant="outline" size="sm" asChild>
                          <a href={matrixStatus.elementMobileUrl} target="_blank" rel="noreferrer">
                            <Smartphone className="h-4 w-4" />
                            Element oeffnen
                          </a>
                        </Button>
                      )}
                      <Button variant="outline" size="sm" asChild>
                        <a href={matrixStatus.elementDownloadUrl} target="_blank" rel="noreferrer">
                          <Smartphone className="h-4 w-4" />
                          App laden
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <Label className="text-sm">Matrix-Zugang</Label>
                    <p className="text-xs text-muted-foreground">
                      {matrixStatus?.reason || "Aktiviert deinen Chat-Zugang fuer Cinny und Element."}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => matrixActivateMutation.mutate()}
                    disabled={!matrixStatus?.canActivate || matrixActivateMutation.isPending}
                  >
                    {matrixActivateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aktivieren"}
                  </Button>
                </div>
              )}
            </div>
              </>
            )}

            {isKsuiteEnabled && (
              <>
            <Separator />

            {/* kSuite Connection */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <rect width="24" height="24" rx="4" fill="#0098FF" />
                    <text x="4" y="18" fill="white" fontSize="14" fontWeight="bold" fontFamily="sans-serif">k</text>
                  </svg>
                  kSuite (Infomaniak)
                </div>
                {ksuiteStatus?.isConnected && (
                  <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                    <CheckCircle2 className="mr-1 h-3 w-3" />
                    {ksuiteStatus.displayName || ksuiteStatus.email}
                  </Badge>
                )}
              </div>

              {ksuiteStatus?.isConnected ? (
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <span className="text-sm text-muted-foreground">
                    Verbunden als {ksuiteStatus.email}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => ksuiteDisconnectMutation.mutate()}
                    disabled={ksuiteDisconnectMutation.isPending}
                    className="text-destructive hover:text-destructive"
                  >
                    {ksuiteDisconnectMutation.isPending ? (
                      <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    ) : (
                      <Unlink className="mr-1 h-3 w-3" />
                    )}
                    Trennen
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Key className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type={showKsuiteToken ? "text" : "password"}
                        placeholder="Infomaniak API-Token..."
                        value={ksuiteToken}
                        onChange={(e) => setKsuiteToken(e.target.value)}
                        className="pl-9 pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => setShowKsuiteToken(!showKsuiteToken)}
                      >
                        {showKsuiteToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => ksuiteSaveMutation.mutate(ksuiteToken)}
                      disabled={!ksuiteToken || ksuiteSaveMutation.isPending}
                    >
                      {ksuiteSaveMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Verbinden"
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Token erstellen im{" "}
                    <a
                      href="https://manager.infomaniak.com/v3/ng/profile/user/token/list"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline inline-flex items-center gap-0.5"
                    >
                      Infomaniak Manager
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </p>
                </div>
              )}
            </div>
              </>
            )}
            {/* Easybill API-Key */}
            {myEmployee && (
              <>
                <Separator />
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Receipt className="h-4 w-4" />
                      Easybill
                    </div>
                    {myEmployee.easybillApiKeySet && (
                      <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        Verbunden
                      </Badge>
                    )}
                  </div>

                  {easybillLoginOptions.length > 0 ? (
                    <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:bg-amber-950/20">
                      <p className="text-sm font-medium">Welcher Easybill-Login bist du?</p>
                      <p className="text-xs text-muted-foreground">
                        Dein Key gehört zu einem Konto mit mehreren Logins und ließ sich nicht eindeutig über deine
                        E-Mail zuordnen. Wähle deinen eigenen Login — nur darauf werden deine Zeiten gebucht.
                      </p>
                      <div className="flex gap-2">
                        <Select value={easybillSelectedLogin} onValueChange={setEasybillSelectedLogin}>
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Login auswählen..." />
                          </SelectTrigger>
                          <SelectContent>
                            {easybillLoginOptions.map((lg) => (
                              <SelectItem key={lg.id} value={lg.id}>
                                {[lg.firstName, lg.lastName].filter(Boolean).join(" ") || lg.id}
                                {lg.email ? ` (${lg.email})` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button size="sm"
                          onClick={() => easybillLoginMutation.mutate(easybillSelectedLogin)}
                          disabled={!easybillSelectedLogin || easybillLoginMutation.isPending}
                        >
                          {easybillLoginMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Übernehmen"}
                        </Button>
                      </div>
                    </div>
                  ) : myEmployee.easybillApiKeySet ? (
                    <div className="flex items-center justify-between rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <span className="text-sm text-muted-foreground">API-Key hinterlegt</span>
                        <p className="text-xs text-muted-foreground">Projektzeiten werden automatisch synchronisiert.</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive"
                          onClick={() => easybillSaveMutation.mutate(null)}
                          disabled={easybillSaveMutation.isPending}
                        >
                          {easybillSaveMutation.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Unlink className="mr-1 h-3 w-3" />}
                          Trennen
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Key className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            type={showEasybillKey ? "text" : "password"}
                            placeholder="Easybill API-Key..."
                            value={easybillKey}
                            onChange={(e) => setEasybillKey(e.target.value)}
                            className="pl-9 pr-10"
                          />
                          <Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full px-3"
                            onClick={() => setShowEasybillKey(!showEasybillKey)}
                          >
                            {showEasybillKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                        <Button size="sm"
                          onClick={() => easybillSaveMutation.mutate(easybillKey)}
                          disabled={!easybillKey || easybillSaveMutation.isPending}
                        >
                          {easybillSaveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verbinden"}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Fuer die automatische Synchronisation von Projektzeiten mit Easybill.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
            </div>
          )}

          {isMattermostEnabled && <MattermostPasswordSync />}
        </div>

        <DialogFooter className="border-t px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}


function MattermostPasswordSync() {
  const { toast } = useToast()
  const [showSync, setShowSync] = useState(false)
  const [syncPassword, setSyncPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)

  const syncMutation = useMutation({
    mutationFn: (password: string) => api.mattermost.syncPassword(password),
    onSuccess: () => {
      toast({ title: "Mattermost-Passwort synchronisiert", description: "Du kannst dich jetzt mit diesem Passwort in Mattermost anmelden." })
      setSyncPassword("")
      setShowSync(false)
    },
    onError: (err: any) => {
      toast({ title: "Fehler", description: err?.response?.data?.detail || "Sync fehlgeschlagen", variant: "destructive" })
    },
  })

  return (
    <div>
      <Separator className="my-3" />
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-blue-600" />
            <Label className="font-medium">Mattermost</Label>
          </div>
          {!showSync && (
            <Button size="sm" variant="outline" onClick={() => setShowSync(true)}>
              <Key className="mr-1 h-3 w-3" /> Passwort synchronisieren
            </Button>
          )}
        </div>
        {showSync && (
          <div className="space-y-2 p-3 border rounded-md bg-muted/30">
            <p className="text-xs text-muted-foreground">
              Gib dein aktuelles NetzERP-Passwort ein um es mit Mattermost zu synchronisieren.
              Danach kannst du dich in der Mattermost-App mit diesem Passwort anmelden.
            </p>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Aktuelles Passwort..."
                  value={syncPassword}
                  onChange={(e) => setSyncPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button
                size="sm"
                disabled={syncPassword.length < 8 || syncMutation.isPending}
                onClick={() => syncMutation.mutate(syncPassword)}
              >
                {syncMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sync"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowSync(false); setSyncPassword("") }}>
                Abbrechen
              </Button>
            </div>
          </div>
        )}
        {!showSync && (
          <p className="text-xs text-muted-foreground">
            Nach einer Passwortaenderung in NetzERP muss das Mattermost-Passwort manuell synchronisiert werden.
          </p>
        )}
      </div>
    </div>
  )
}
